import { useEffect, useRef } from "react";

// A faint, continuously-scrolling candlestick "tape" for the hero background.
export default function HeroBackgroundChart() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const STEP = 24;
    const CW = 13;
    let w = 0;
    let h = 0;
    let candles = [];
    let price = 100;
    let offset = 0;
    let raf;
    let last = performance.now();

    const mk = (prev) => {
      const o = prev;
      const c = o + (Math.random() - 0.5) * 6;
      const hi = Math.max(o, c) + Math.random() * 3;
      const lo = Math.min(o, c) - Math.random() * 3;
      return { o, c, h: hi, l: lo };
    };

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const need = Math.ceil(w / STEP) + 4;
      while (candles.length < need) {
        price = Math.max(40, Math.min(160, price + (Math.random() - 0.5) * 8));
        candles.push(mk(price));
        price = candles[candles.length - 1].c;
      }
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      let mn = Infinity;
      let mx = -Infinity;
      candles.forEach((k) => {
        if (k.l < mn) mn = k.l;
        if (k.h > mx) mx = k.h;
      });
      const pad = (mx - mn) * 0.18 || 1;
      mn -= pad;
      mx += pad;
      const y = (v) => h - ((v - mn) / (mx - mn)) * h;

      // faint grid
      ctx.strokeStyle = "rgba(255,255,255,0.025)";
      ctx.lineWidth = 1;
      for (let g = 1; g < 6; g++) {
        const yy = (h * g) / 6;
        ctx.beginPath();
        ctx.moveTo(0, yy);
        ctx.lineTo(w, yy);
        ctx.stroke();
      }

      // candles
      candles.forEach((k, i) => {
        const cx = i * STEP - offset;
        if (cx < -STEP || cx > w + STEP) return;
        const up = k.c >= k.o;
        const col = up ? "126,224,161" : "255,122,122";
        ctx.strokeStyle = `rgba(${col},0.16)`;
        ctx.fillStyle = `rgba(${col},0.11)`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx + CW / 2, y(k.h));
        ctx.lineTo(cx + CW / 2, y(k.l));
        ctx.stroke();
        const top = y(Math.max(k.o, k.c));
        const bh = Math.max(Math.abs(y(k.o) - y(k.c)), 1);
        ctx.fillRect(cx, top, CW, bh);
        ctx.strokeRect(cx, top, CW, bh);
      });
    };

    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (!reduce) {
        offset += dt * 20;
        while (offset >= STEP) {
          offset -= STEP;
          candles.shift();
          price = Math.max(40, Math.min(160, candles[candles.length - 1].c + (Math.random() - 0.5) * 8));
          candles.push(mk(price));
        }
      }
      draw();
    };
    loop(last);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} style={{ width: "100%", height: "100%", display: "block" }} data-testid="hero-bg-chart" />;
}
